// Simple client-side dominant color extractor using <canvas>.
// Given an image URL, samples pixels, quantizes to a small palette, and returns
// a curated set of colors sorted by luminance: { dark, mid, light, accent }
// All returned values are #RRGGBB hex strings.

function toHex(n) {
  const h = Math.max(0, Math.min(255, Math.round(n))).toString(16);
  return h.length === 1 ? "0" + h : h;
}
function rgbToHex(r, g, b) { return "#" + toHex(r) + toHex(g) + toHex(b); }
function relLum(r, g, b) { return (0.299 * r + 0.587 * g + 0.114 * b) / 255; }
function saturation(r, g, b) {
  const max = Math.max(r, g, b) / 255;
  const min = Math.min(r, g, b) / 255;
  if (max === 0) return 0;
  return (max - min) / max;
}

// Quantize a channel to K buckets so similar colors merge together
function quantize(v, step = 32) { return Math.round(v / step) * step; }

export async function extractPaletteFromImage(url, { sampleCount = 4000 } = {}) {
  if (!url) throw new Error("No image URL provided");
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const w = Math.min(120, img.naturalWidth);
        const h = Math.round((img.naturalHeight / img.naturalWidth) * w) || 80;
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, w, h);
        const data = ctx.getImageData(0, 0, w, h).data;

        // Bucket colors and count occurrences
        const buckets = new Map();
        const total = w * h;
        const step = Math.max(1, Math.floor(total / sampleCount));
        for (let i = 0; i < total; i += step) {
          const p = i * 4;
          const a = data[p + 3];
          if (a < 200) continue;
          const r = data[p], g = data[p + 1], b = data[p + 2];
          // skip near-white/near-black/very desaturated (backgrounds and noise)
          const lum = relLum(r, g, b);
          if (lum > 0.96 || lum < 0.04) continue;
          const key = `${quantize(r)}-${quantize(g)}-${quantize(b)}`;
          const cur = buckets.get(key);
          if (cur) {
            cur.count += 1;
            cur.r += r; cur.g += g; cur.b += b;
          } else {
            buckets.set(key, { count: 1, r, g, b });
          }
        }
        if (buckets.size === 0) throw new Error("No usable colors");

        // Average each bucket + rank
        const swatches = Array.from(buckets.values()).map((c) => {
          const r = c.r / c.count, g = c.g / c.count, b = c.b / c.count;
          return { r, g, b, count: c.count, lum: relLum(r, g, b), sat: saturation(r, g, b), hex: rgbToHex(r, g, b) };
        });
        swatches.sort((a, b) => b.count - a.count);
        const top = swatches.slice(0, 10);

        // Pick a "dark" (low lum, ideally with some warmth), a "light" (high lum), and an accent (saturated mid)
        const byLum = [...top].sort((a, b) => a.lum - b.lum);
        const bySat = [...top].sort((a, b) => b.sat - a.sat);

        const dark = byLum[0] || top[0];
        const light = byLum[byLum.length - 1] || top[0];
        // accent: most saturated in middle luminance range
        const midCands = top.filter((c) => c.lum > 0.25 && c.lum < 0.75);
        const accent = (midCands.length ? midCands : bySat).sort((a, b) => b.sat - a.sat)[0] || top[0];
        const mid = top[Math.floor(top.length / 2)] || top[0];

        resolve({
          dark: dark.hex,
          light: light.hex,
          mid: mid.hex,
          accent: accent.hex,
          all: top.map((s) => s.hex),
        });
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = url;
  });
}

// ---------- Full theme derivation ----------
// Turns a raw palette into a complete, legible color scheme for the invitation
// (and for the editor UI): text, accent, page background, surfaces and muted text.

export function hexToRgb(hex) {
  if (!hex) return null;
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex).trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

export function rgbChannels(hex) {
  const c = hexToRgb(hex);
  return c ? `${c.r} ${c.g} ${c.b}` : null;
}

function mixHex(a, b, t) {
  const A = hexToRgb(a), B = hexToRgb(b);
  if (!A || !B) return a;
  return rgbToHex(A.r + (B.r - A.r) * t, A.g + (B.g - A.g) * t, A.b + (B.b - A.b) * t);
}

function lumOf(hex) {
  const c = hexToRgb(hex);
  return c ? relLum(c.r, c.g, c.b) : 0.5;
}

function clampLum(hex, min, max) {
  const l = lumOf(hex);
  if (l < min) return mixHex(hex, "#FFFFFF", Math.min(0.9, (min - l) / Math.max(0.001, 1 - l)));
  if (l > max) return mixHex(hex, "#000000", Math.min(0.9, (l - max) / Math.max(0.001, l)));
  return hex;
}

function rgbToHsl(hex) {
  const c = hexToRgb(hex);
  if (!c) return { h: 0, s: 0, l: 0.5 };
  const r = c.r / 255, g = c.g / 255, b = c.b / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return { h, s, l };
}

function hslToHex(h, s, l) {
  const f = (n) => {
    const k = (n + h * 12) % 12;
    const a = s * Math.min(l, 1 - l);
    return l - a * Math.max(-1, Math.min(k - 3, Math.min(9 - k, 1)));
  };
  return rgbToHex(f(0) * 255, f(8) * 255, f(4) * 255);
}

// Same hue, but clearly colorful — used as the tint base for backgrounds
function vivid(hex) {
  const { h, s } = rgbToHsl(hex);
  return hslToHex(h, Math.max(0.55, Math.min(0.95, s * 1.3)), 0.58);
}

export function buildThemeFromPalette(palette) {
  const p = palette || {};
  const list = p.all && p.all.length ? p.all : [p.accent || "#C5A880"];
  const dominant = list[0];
  // Accent: vivid but readable over a light background
  const accent = clampLum(p.accent || dominant, 0.26, 0.58);
  // Text: deep version of the darkest tone in the image (keeps the image's hue)
  const text = clampLum(p.dark || dominant, 0.05, 0.2);
  // Page background & surfaces: soft, clearly visible tint of the accent hue
  const tint = vivid(accent);
  const bg = mixHex("#FFFFFF", tint, 0.1);
  const surface = mixHex("#FFFFFF", tint, 0.2);
  const muted = mixHex(text, bg, 0.32);
  return {
    text_color: text,
    accent_color: accent,
    bg_color: bg,
    surface_color: surface,
    muted_color: muted,
  };
}

// Three ready-to-use interpretations of the same image palette.
export function buildThemeVariants(palette) {
  const p = palette || {};
  const list = p.all && p.all.length ? p.all : [p.accent || "#C5A880"];
  const dominant = list[0];
  const baseAccent = p.accent || dominant;
  const { h, s } = rgbToHsl(baseAccent);

  const soft = buildThemeFromPalette(p);

  // Vibrante — accent al máximo, fondo casi blanco para que el color explote
  const vibAccent = hslToHex(h, Math.max(0.72, Math.min(1, s * 1.5)), 0.44);
  const vibText = clampLum(p.dark || dominant, 0.04, 0.16);
  const vibBg = mixHex("#FFFFFF", vivid(vibAccent), 0.04);
  const vibrant = {
    accent_color: vibAccent,
    text_color: vibText,
    bg_color: vibBg,
    surface_color: mixHex("#FFFFFF", vivid(vibAccent), 0.12),
    muted_color: mixHex(vibText, vibBg, 0.34),
  };

  // Oscura — modo noche con el mismo tono de la imagen
  const darkBg = hslToHex(h, Math.min(0.45, Math.max(0.14, s * 0.6)), 0.1);
  const darkSurface = hslToHex(h, Math.min(0.42, Math.max(0.12, s * 0.55)), 0.17);
  const darkAccent = hslToHex(h, Math.max(0.5, Math.min(0.9, s * 1.2)), 0.66);
  const darkText = mixHex("#FFFFFF", vivid(darkAccent), 0.06);
  const dark = {
    accent_color: darkAccent,
    text_color: darkText,
    bg_color: darkBg,
    surface_color: darkSurface,
    muted_color: mixHex(darkText, darkBg, 0.4),
  };

  return [
    { id: "suave", label: "Suave", description: "Tonos claros y elegantes", ...soft },
    { id: "vibrante", label: "Vibrante", description: "Color intenso y alegre", ...vibrant },
    { id: "oscura", label: "Oscura", description: "Fondo de noche", ...dark },
  ];
}

export async function buildThemeFromImage(url) {
  const palette = await extractPaletteFromImage(url);
  return { ...buildThemeFromPalette(palette), palette, variants: buildThemeVariants(palette) };
}

// ---------- Background suggestions with guaranteed legibility ----------

function srgbLum(hex) {
  const c = hexToRgb(hex);
  if (!c) return 1;
  const f = (v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(c.r) + 0.7152 * f(c.g) + 0.0722 * f(c.b);
}

export function contrastRatio(a, b) {
  const l1 = srgbLum(a), l2 = srgbLum(b);
  const hi = Math.max(l1, l2), lo = Math.min(l1, l2);
  return (hi + 0.05) / (lo + 0.05);
}

// Six curated backgrounds built from the image hue, each paired with a text color
// that always keeps a comfortable contrast (>= 4.5:1).
export function buildBackgroundOptions(palette, currentText, currentAccent) {
  const p = palette || {};
  const list = p.all && p.all.length ? p.all : [p.accent || "#C5A880"];
  const base = p.accent || list[0];
  const { h, s } = rgbToHsl(base);
  const sat = Math.min(0.6, Math.max(0.18, s));

  const specs = [
    { id: "blanco", label: "Blanco cálido", hex: mixHex("#FFFFFF", vivid(base), 0.03) },
    { id: "crema", label: "Crema", hex: mixHex("#FFFFFF", vivid(base), 0.1) },
    { id: "tinte", label: "Tinte suave", hex: mixHex("#FFFFFF", vivid(base), 0.2) },
    { id: "pastel", label: "Pastel", hex: mixHex("#FFFFFF", vivid(base), 0.34) },
    { id: "profundo", label: "Profundo", hex: hslToHex(h, sat * 0.7, 0.19) },
    { id: "noche", label: "Noche", hex: hslToHex(h, sat * 0.6, 0.1) },
  ];

  const darkInk = hslToHex(h, Math.min(0.35, sat), 0.12);
  const lightInk = mixHex("#FFFFFF", vivid(base), 0.06);

  return specs.map((sp) => {
    const bg = sp.hex;
    const keepsCurrent = currentText && contrastRatio(bg, currentText) >= 4.5;
    const fallback = contrastRatio(bg, darkInk) >= contrastRatio(bg, lightInk) ? darkInk : lightInk;
    const text = keepsCurrent ? currentText : fallback;
    const isDarkBg = srgbLum(bg) < 0.35;
    return {
      ...sp,
      bg_color: bg,
      text_color: text,
      accent_color: legibleAccent(currentAccent || base, bg),
      surface_color: isDarkBg ? mixHex(bg, "#FFFFFF", 0.09) : mixHex(bg, vivid(base), 0.12),
      muted_color: mixHex(text, bg, 0.35),
      contrast: Math.round(contrastRatio(bg, text) * 10) / 10,
      adjustedText: !keepsCurrent,
    };
  });
}

// Keeps the accent hue but pushes its lightness until it stands out over the background
function legibleAccent(accent, bg) {
  const { h, s } = rgbToHsl(accent);
  if (contrastRatio(bg, accent) >= 3) return accent;
  const goLighter = srgbLum(bg) < 0.35;
  let best = accent, bestRatio = contrastRatio(bg, accent);
  for (let i = 1; i <= 9; i++) {
    const l = goLighter ? Math.min(0.92, 0.5 + i * 0.05) : Math.max(0.14, 0.5 - i * 0.045);
    const cand = hslToHex(h, Math.max(0.35, s), l);
    const r = contrastRatio(bg, cand);
    if (r > bestRatio) { best = cand; bestRatio = r; }
    if (r >= 3.5) return cand;
  }
  return best;
}
